import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, User, Users, Building2, Sparkles, ArrowRight } from "lucide-react";

type Plan = 'PERSONAL' | 'PRO' | 'STUDIO' | 'ENTERPRISE';

interface UpgradePromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: string;
  currentPlan: Plan;
  requiredPlan: Plan;
  description?: string;
}

const planMeta: Record<Plan, { icon: React.ReactNode; color: string; bgColor: string; label: string }> = {
  PERSONAL: { icon: <User className="w-5 h-5" />, color: 'text-slate-400', bgColor: 'bg-slate-500/10', label: 'Personal' },
  PRO: { icon: <Crown className="w-5 h-5" />, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Pro' },
  STUDIO: { icon: <Users className="w-5 h-5" />, color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'Studio' },
  ENTERPRISE: { icon: <Building2 className="w-5 h-5" />, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Enterprise' },
};

const featureDescriptions: Record<string, string> = {
  'personal_room': 'Personal rooms allow you to have a dedicated room with a custom URL that persists across sessions.',
  'team_members': 'Invite team members to collaborate with you on your projects.',
  'connectors': 'Connect external services like Frame.io, Google Drive, and Dropbox.',
  'more_rooms': 'Upgrade to get more personal rooms for your different meeting needs.',
};

export function UpgradePromptDialog({
  open,
  onOpenChange,
  feature,
  currentPlan,
  requiredPlan,
  description,
}: UpgradePromptDialogProps) {
  const required = planMeta[requiredPlan];
  
  const handleUpgrade = () => {
    onOpenChange(false);
    const upgradeWindow = window.open('/settings?section=credits&creditsTab=manage-plan', '_blank', 'noopener,noreferrer');
    if (!upgradeWindow) {
      window.location.assign('/settings?section=credits&creditsTab=manage-plan');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className={`mx-auto w-16 h-16 rounded-full ${required.bgColor} flex items-center justify-center ${required.color} mb-4`}>
            {required.icon || <Sparkles className="w-8 h-8" />}
          </div>
          <DialogTitle className="text-xl">
            Upgrade to {required.label}
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {description || featureDescriptions[feature] || `This feature requires the ${required.label} plan or higher.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current vs Required */}
          <div className="flex items-center justify-center gap-4 text-sm">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted">
              <span className="text-muted-foreground">Current:</span>
              <span className="font-medium">{planMeta[currentPlan].label}</span>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${required.bgColor}`}>
              <span className={`${required.color}`}>Required:</span>
              <span className={`font-medium ${required.color}`}>{required.label}</span>
            </div>
          </div>

          {/* Benefits preview */}
          {requiredPlan === 'PRO' && (
            <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="font-medium text-foreground">Pro includes:</p>
              <ul className="space-y-1">
                <li>• <span className="line-through text-muted-foreground/70">1,170</span> <span className="text-amber-500 font-medium">1,800</span> monthly credits</li>
                <li>• 1 personal room</li>
                <li>• Connectors (Frame.io, etc.)</li>
                <li>• Priority support</li>
              </ul>
            </div>
          )}
          {requiredPlan === 'STUDIO' && (
            <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="font-medium text-foreground">Studio includes:</p>
              <ul className="space-y-1">
                <li>• <span className="line-through text-muted-foreground/70">2,970</span> <span className="text-purple-500 font-medium">5,400</span> monthly credits</li>
                <li>• Up to 5 team members</li>
                <li>• Up to 5 personal rooms</li>
                <li>• Brand rooms with your logo and accent colors</li>
                <li>• Everything in Pro</li>
              </ul>
            </div>
          )}
          {requiredPlan === 'ENTERPRISE' && (
            <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="font-medium text-foreground">Enterprise includes:</p>
              <ul className="space-y-1">
                <li>• <span className="text-blue-500 font-medium">10,000+</span> monthly credits</li>
                <li>• Unlimited team members</li>
                <li>• Unlimited personal rooms</li>
                <li>• Custom integrations</li>
                <li>• Dedicated support</li>
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            Maybe Later
          </Button>
          <Button 
            className="flex-1"
            onClick={handleUpgrade}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            View Plans
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
